<template>
    <div class="container-parent d-flex align-center justify-center tw-h-full">
        <v-container>
            <div class="tw-text-center">
                <v-icon
                    size="60"
                    color="secondary"
                    class="tw-mb-6">
                    mdi-check-circle-outline
                </v-icon>

                <h2 class="tw-font-extrabold tw-text-center tw-text-secondary tw-text-4xl tw-underline tw-underline-offset-0 tw-decoration-primary tw-mb-6">
                    {{ $t('auth.accountConfirmed.title') }}
                </h2>

                <h3 class="tw-font-semibold tw-text-center tw-text-secondary tw-text-2xl tw-mb-8">
                    {{ $t('auth.accountConfirmed.subtitle') }}
                </h3>

                <p class="tw-text-center tw-text-gray-600 tw-text-lg tw-mb-8">
                    {{ $t('auth.accountConfirmed.redirectMessage') }}
                </p>

                <div class="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mb-8">
                    <v-progress-circular
                        :model-value="progress"
                        color="primary"
                        size="24"
                        width="3"
                        class="tw-mr-2">
                    </v-progress-circular>
                    <span class="tw-text-sm tw-text-gray-600">
                        {{ $t('auth.accountConfirmed.redirectingIn', { seconds: countdown }) }}
                    </span>
                </div>

                <div class="tw-flex tw-justify-center">
                    <v-btn
                        :loading="isRedirecting"
                        class="bg-primary text-black font-weight-bold !tw-px-12 !tw-max-h-none !tw-h-auto !tw-py-4"
                        rounded="xl"
                        size="x-large"
                        color="primary"
                        @click="redirectToLogin">
                        {{ $t('auth.accountConfirmed.goToLogin') }}
                    </v-btn>
                </div>
            </div>

            <div class="tw-flex tw-text-center tw-justify-center tw-w-14 tw-mx-auto tw-mt-8 tw-gap-x-4">
                <span
                    class="fi fi-pt hover:tw-cursor-pointer"
                    @click="setLanguage('pt')"></span>
                <span
                    class="fi fi-gb hover:tw-cursor-pointer"
                    @click="setLanguage('en')"></span>
            </div>
        </v-container>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted, onUnmounted } from 'vue';
    import { useRouter } from 'vue-router';
    import { useI18n } from 'vue-i18n';
    import { useLanguageStore } from '@/store/modules/language';

    const router = useRouter();
    const i18n = useI18n();
    const store = useLanguageStore();

    const countdown = ref(5);
    const progress = ref(100);
    const isRedirecting = ref(false);
    let countdownInterval: NodeJS.Timeout | null = null;

    const setLanguage = (language: string) => {
        i18n.locale.value = language;
        store.setLanguage(language);
    };

    const redirectToLogin = async () => {
        isRedirecting.value = true;
        await router.push({ name: 'Auth-Login' });
    };

    const startCountdown = () => {
        countdownInterval = setInterval(() => {
            countdown.value--;
            progress.value = (countdown.value / 5) * 100;

            if (countdown.value <= 0) {
                clearInterval(countdownInterval!);
                redirectToLogin();
            }
        }, 1000);
    };

    onMounted(() => {
        setLanguage(store.language);
        startCountdown();
    });

    onUnmounted(() => {
        if (countdownInterval) {
            clearInterval(countdownInterval);
        }
    });
</script>

<style scoped>
    .container-parent {
        max-width: min(90%, 31.25rem);
        margin-inline: auto;
    }

    h2.tw-underline {
        text-decoration-skip-ink: none;
    }
</style>
